import { forwardRef, useCallback, useMemo, useRef, useState } from "react";
import { formatInTimeZone } from "date-fns-tz";
import React from "react";
import { Device, Facility, FacilityEvent, Target } from "../../models";
import { FacilityEvents, getApiUrl } from "../../api";
import { FacilityEventType, getFacilityEventTypeDisplayName } from "../../models/Shared.FacilityEventType.gen";
import { getTargetTypeDisplayName } from "../../models/Shared.TargetType.gen";
import { getFHWAClassDisplayName } from "../../models/Shared.FHWAClass.gen";
import { getLaneDisplayName } from "../../models/Shared.Lane.gen";
import { getTurnTypeDisplayName } from "../../models/Shared.TurnType.gen";
import { useConfirmDialog } from "../../hooks";
import { useInfiniteEvents } from "@app/dashboard/src/hooks";
import { EventSnapshot, Icons, ImageLightbox, SortableTable, SortableTableHeaderType, InfiniteScrollOptions, Link } from "@app/dashboard/src/components/shared";
import { FilterOption } from "@app/dashboard/src/components/shared/Filter";
import { toastSuccess } from "@app/dashboard/src/helpers";
import { LiveViewMode } from "./LiveViewProvider";
import { getEventTriggerName } from "./eventDetailsHelpers";

export interface EventsTableProps {
    mode: LiveViewMode;
    facility: Facility;
    devices: Device[];
    facilityId?: string;
    timezoneId?: string;
    localEvents?: FacilityEvent[];
    localTargets?: Target[];
    onRowClick?: (event: FacilityEvent) => void;
    onViewTarget?: (event: FacilityEvent, target: Target | undefined) => void;
    /** Facility mode only: show a per-row delete button (with a confirm prompt). */
    enableDelete?: boolean;
    /** Called after an event is successfully deleted. */
    onEventDeleted?: (event: FacilityEvent) => void;
}

type EventQuery = {
    limit: number;
    sortKey?: string;
    sortDirection?: "asc" | "desc";
    filters?: FilterOption<FacilityEvent>[];
    cursor?: string;
};

export interface EventsTableMethods {
    // Resolves once the export has been fetched and handed to the browser, so callers can show
    // progress for its duration. In facility mode this is a limit=50000 request that can run for
    // tens of seconds; it rejects if that request fails, and callers are expected to handle it.
    downloadCSV: () => Promise<void>;
}

export const EventsTable = forwardRef<EventsTableMethods, EventsTableProps>(({ mode, facility, devices, facilityId, timezoneId, localEvents, localTargets, onRowClick, onViewTarget, enableDelete, onEventDeleted }, ref) => {
    const isDeviceMode = mode === "device";
    const confirmDelete = useConfirmDialog();

    const [lightboxUrl, setLightboxUrl] = useState<string | undefined>();

    const [localSortKey, setLocalSortKey] = useState<string>("dateCreated");
    const [localSortDirection, setLocalSortDirection] = useState<"asc" | "desc">("desc");
    const [localFilters, setLocalFilters] = useState<FilterOption<FacilityEvent>[]>([]);

    const [query, setQuery] = useState<EventQuery>({
        limit: 50,
        sortKey: "dateCreated",
        sortDirection: "desc",
    });

    const convertFiltersToParams = (filters?: FilterOption<FacilityEvent>[]) => {
        const params: { eventType?: number; deviceId?: string; trigger?: string; eventValue?: number; dateFrom?: Date; dateTo?: Date } = {};
        if (!filters) return params;
        filters.forEach((filter) => {
            if (filter.value === undefined || filter.value === "") return;
            switch (filter.id) {
                case "eventType":
                    params.eventType = parseInt(filter.value);
                    break;
                case "deviceId":
                    params.deviceId = filter.value;
                    break;
                case "screenLineId":
                    params.trigger = filter.value;
                    break;
                case "eventValue":
                    params.eventValue = parseInt(filter.value);
                    break;
                case "dateCreated":
                    if (filter.value && typeof filter.value === "object") {
                        if (filter.value.from) params.dateFrom = new Date(filter.value.from);
                        if (filter.value.to) params.dateTo = new Date(filter.value.to);
                    }
                    break;
            }
        });
        return params;
    };

    const fetchPage = useCallback(
        (cursor?: string) => {
            if (!facilityId) return Promise.resolve(undefined);
            const p = convertFiltersToParams(query.filters);
            return FacilityEvents.getByFacilityPaginated(facilityId, 50, query.sortKey, query.sortDirection, p.eventType, p.deviceId, p.trigger, p.eventValue, p.dateFrom, p.dateTo, cursor);
        },
        [facilityId, query]
    );

    const { events: apiEvents, setEvents: setApiEvents, isLoading, hasMore, loadMore } = useInfiniteEvents(fetchPage, !isDeviceMode && !!facilityId);

    const zone = timezoneId ?? facility?.localTimezoneId ?? Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).resolvedOptions().timeZone;
    const timeZone = useMemo(() => formatInTimeZone(new Date(), zone, "z"), [zone]);

    const getDeviceName = (deviceId?: string) => devices?.find((d) => d.id === deviceId)?.name ?? "";
    const getTriggerName = (event: FacilityEvent) => getEventTriggerName(event, facility) ?? "";

    const formatEventValue = (event: FacilityEvent) => {
        switch (event.eventType) {
            case FacilityEventType.ScreenLineChange:
            case FacilityEventType.DetectionZoneChange:
                return event.eventValue >= 1 ? "Entry" : "Exit";
            case FacilityEventType.DetectionZonePresence:
                return (event.eventValue ? event.eventValue : "No") + " Target" + (event.eventValue !== 1 ? "s" : "");
            case FacilityEventType.Speeding:
                return ((event.eventValue ?? 0) * 2.23694).toFixed(1) + " mph";
            case FacilityEventType.DetectionZoneDwell: {
                // EV1 = configured threshold (legacy meaning, preserved).
                // EV2 = actual observed dwell (new, additive). Legacy rows
                // only know they exceeded the threshold → ">N seconds".
                const actual = event.eventValue2;
                const threshold = event.eventValue ?? 0;
                const seconds = actual ?? threshold;
                const prefix = actual != null ? "" : ">";
                return `${prefix}${seconds} second${seconds !== 1 ? "s" : ""}`;
            }
            case FacilityEventType.WrongWay:
                return event.eventValue + "°";
            case FacilityEventType.NearMiss: {
                // EventValue = NearMissSeverity (1..5). EventValue2 packs (TTC centiseconds * 1000) + conflictAngleDegrees.
                const severity = event.eventValue ?? 0;
                const ev2 = event.eventValue2 ?? 0;
                const ttc = (Math.floor(ev2 / 1000) / 100).toFixed(2);
                const angle = ev2 % 1000;
                const severityLabels = ["", "Minor", "Moderate", "Serious", "Severe", "Critical"];
                const conflictType = angle < 30 ? "Rear-end" : angle < 85 ? "Sideswipe" : "Crossing";
                return `${severityLabels[severity] ?? "?"} (${conflictType}, TTC: ${ttc}s)`;
            }
            default:
                return event.eventValue?.toString() ?? "";
        }
    };

    const findTargetForEvent = (event: FacilityEvent): Target | undefined => {
        const targetId = event.externalTargetId || (event as any)?.targetId;
        if (!targetId || !localTargets) return undefined;
        return localTargets.find((t) => t.id === targetId || t.externalId === targetId);
    };

    const getTimestamp = (date: Date | string | undefined): number => {
        if (!date) return 0;
        if (date instanceof Date) return date.getTime();
        if (typeof date === "string") return new Date(date).getTime();
        return 0;
    };

    const getEventKey = (event: FacilityEvent, index: number): string => {
        const id = event.id;
        if (!id || id === "00000000-0000-0000-0000-000000000000") {
            return `event-${getTimestamp(event.dateCreated)}-${event.eventType}-${index}`;
        }
        return id;
    };

    const events = useMemo(() => {
        if (isDeviceMode) {
            let filtered = [...(localEvents || [])];

            const activeFilters = localFilters.filter((f) => f.value !== undefined && f.value !== "");
            if (activeFilters.length > 0) {
                filtered = filtered.filter((event) => {
                    return activeFilters.every((filter) => {
                        const filterValue = filter.value;
                        switch (filter.id) {
                            case "eventType":
                                return event.eventType === parseInt(filterValue);
                            case "eventValue":
                                return event.eventValue === parseInt(filterValue);
                            default:
                                return true;
                        }
                    });
                });
            }

            filtered.sort((a, b) => {
                if (localSortKey === "dateCreated") {
                    const aTime = getTimestamp(a.dateCreated);
                    const bTime = getTimestamp(b.dateCreated);
                    return localSortDirection === "asc" ? aTime - bTime : bTime - aTime;
                }
                const aVal = a[localSortKey as keyof FacilityEvent];
                const bVal = b[localSortKey as keyof FacilityEvent];
                if (typeof aVal === "number" && typeof bVal === "number") {
                    return localSortDirection === "asc" ? aVal - bVal : bVal - aVal;
                }
                return 0;
            });

            return filtered.map((event, index) => ({
                ...event,
                id: getEventKey(event, index),
            }));
        }
        return apiEvents;
    }, [isDeviceMode, localEvents, apiEvents, localSortKey, localSortDirection, localFilters]);

    const infiniteScrollOptions: InfiniteScrollOptions = {
        enabled: true,
        onLoadMore: isDeviceMode ? async () => {} : loadMore,
        hasMore: isDeviceMode ? false : hasMore,
        isLoading: isDeviceMode ? false : isLoading,
        overscan: 5,
        // Keep a couple screens of rows mounted above/below the viewport so scrolling back to
        // recently seen rows keeps their loaded thumbnails (no remount, no re-fetch, no flash).
        increaseViewportBy: { top: 2000, bottom: 2000 },
        followOutput: false,
    };

    const eventTypeFilterOptions = Object.values(FacilityEventType)
        .filter((value) => typeof value === "number")
        .map((value) => ({
            value: value.toString(),
            label: getFacilityEventTypeDisplayName(value as FacilityEventType) ?? "",
        }));

    const deviceFilterOptions = devices.map((dev) => ({
        value: dev.id ?? "",
        label: dev.name ?? "Unknown Device",
    }));

    // Kept in a ref so the columns memo doesn't rebuild every render (useConfirmDialog.show is a
    // fresh function each render). Facility mode only — device-mode rows have synthetic ids.
    const handleDeleteEventRef = useRef<(event: FacilityEvent) => void>(() => {});
    handleDeleteEventRef.current = (event: FacilityEvent) => {
        confirmDelete.show("Delete Event", "Are you sure you want to delete this event? This cannot be undone.", "Delete", async () => {
            if (!event.id) return;
            const success = await FacilityEvents.deleteEvent(event.id);
            if (!success) throw new Error("The event could not be deleted.");
            setApiEvents((prev) => prev.filter((e) => e.id !== event.id));
            toastSuccess("Event deleted");
            onEventDeleted?.(event);
        });
    };

    const columns = useMemo(() => {
        const cols: SortableTableHeaderType<FacilityEvent>[] = [
            {
                dataKey: "dateCreated",
                header: `Date (${timeZone})`,
                width: 135,
                filterable: !isDeviceMode,
                filterType: "daterange",
                renderFunc: (event?: FacilityEvent) => {
                    if (!event?.dateCreated) return "";
                    const date = event.dateCreated instanceof Date ? event.dateCreated : new Date(event.dateCreated);
                    return formatInTimeZone(date, zone, "MMM d, HH:mm:ss");
                },
            },
            {
                dataKey: "eventType",
                header: "Event Type",
                width: 120,
                filterable: true,
                filterType: "select",
                filterOptions: eventTypeFilterOptions,
                renderFunc: (event?: FacilityEvent) => (event ? getFacilityEventTypeDisplayName(event.eventType) : ""),
            },
            {
                header: "Target Type",
                width: 110,
                sortable: false,
                renderFunc: (event?: FacilityEvent) => (event?.target ? getTargetTypeDisplayName(event.target.type) ?? "" : ""),
            } as SortableTableHeaderType<FacilityEvent>,
        ];

        // Only show device column in facility mode
        if (!isDeviceMode) {
            cols.push({
                dataKey: "deviceId",
                header: "Device",
                width: 120,
                filterable: true,
                filterType: "select",
                filterOptions: deviceFilterOptions,
                renderFunc: (event?: FacilityEvent) => getDeviceName(event?.deviceId),
            } as SortableTableHeaderType<FacilityEvent>);
        }

        cols.push(
            {
                header: "Trigger",
                width: 120,
                dataKey: "screenLineId",
                renderFunc: (event?: FacilityEvent) => (event ? getTriggerName(event) : ""),
            } as SortableTableHeaderType<FacilityEvent>,
            {
                header: "Value",
                width: 100,
                dataKey: "eventValue",
                filterable: true,
                filterType: "number",
                renderFunc: (event?: FacilityEvent) => (event ? formatEventValue(event) : ""),
            } as SortableTableHeaderType<FacilityEvent>
        );

        // View Target link
        if (onViewTarget) {
            cols.push({
                header: "",
                width: 75,
                renderFunc: (event?: FacilityEvent) => {
                    if (!event) return "";
                    const targetId = event.externalTargetId || (event as any)?.targetId;
                    if (!targetId) return "";
                    const target = isDeviceMode ? findTargetForEvent(event) : undefined;
                    return (
                        <Link
                            onClick={() => {
                                onViewTarget(event, target);
                            }}
                        >
                            View Details
                        </Link>
                    );
                },
            } as SortableTableHeaderType<FacilityEvent>);
        }

        // Delete button (facility mode only — device-mode rows have synthetic ids).
        if (enableDelete && !isDeviceMode) {
            cols.push({
                header: "",
                width: 44,
                sortable: false,
                renderFunc: (event?: FacilityEvent) => {
                    if (!event?.id) return "";
                    return (
                        <button
                            type="button"
                            aria-label="Delete event"
                            title="Delete event"
                            className="p-1 text-muted-body-text hover:text-red-600"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteEventRef.current(event);
                            }}
                        >
                            <Icons.Trash />
                        </button>
                    );
                },
            } as SortableTableHeaderType<FacilityEvent>);
        }

        return cols;
    }, [isDeviceMode, timeZone, zone, eventTypeFilterOptions, deviceFilterOptions, onViewTarget, localTargets, enableDelete]);

    // Insert the snapshot column (right after Date) only when some loaded event actually has a photo.
    const showPhotoColumn = events.some((e) => !!e.attachmentUrl);
    const displayColumns = useMemo<SortableTableHeaderType<FacilityEvent>[]>(() => {
        if (!showPhotoColumn) return columns;
        const photoColumn: SortableTableHeaderType<FacilityEvent> = {
            header: "Photo",
            width: 56,
            sortable: false,
            renderFunc: (event?: FacilityEvent) => <EventSnapshot organizationId={facility?.organizationId} deviceId={event?.deviceId} externalTargetId={event?.externalTargetId} hasPhoto={!!event?.attachmentUrl} onZoom={setLightboxUrl} />,
        };
        return [columns[0], photoColumn, ...columns.slice(1)];
    }, [columns, showPhotoColumn, facility?.organizationId]);

    const downloadCSV = useCallback(async () => {
        let dataToExport: FacilityEvent[] = [];

        if (isDeviceMode) {
            dataToExport = events;
        } else if (facilityId) {
            const filterParams = convertFiltersToParams(query.filters);
            const data = await FacilityEvents.getByFacilityPaginated(facilityId, 50000, query.sortKey, query.sortDirection, filterParams.eventType, filterParams.deviceId, filterParams.trigger, filterParams.eventValue, filterParams.dateFrom, filterParams.dateTo, undefined);
            dataToExport = data?.data || [];
        }

        if (!dataToExport.length) return;

        try {
            const orgId = facility?.organizationId;
            const getImageUrl = (event: FacilityEvent) => {
                if (event.attachmentUrl) return getApiUrl(event.attachmentUrl);
                if (event.target?.hasPhoto && orgId && event.deviceId && event.externalTargetId) {
                    return getApiUrl(`/api/organization/${orgId}/device/${event.deviceId}/target/${event.externalTargetId}/photo`);
                }
                return "";
            };
            const toMph = (v?: number) => (v !== undefined && v !== null ? (v * 2.23694).toFixed(1) : "");

            const headers = ["ID", "Date (UTC)", `Date (${timeZone})`, "Event Type", "Device", "Trigger", "Value", "Target ID", "Target Type", "FHWA Class", "Lane", "Turn", "Max Speed (mph)", "Avg Speed (mph)", "Min Speed (mph)", "Width (m)", "Length (m)", "Image URL"];

            const rows = dataToExport.map((event) => {
                const t = event.target;
                return [
                    event.id ?? "",
                    event.dateCreated?.toISOString().replace("Z", "") ?? "",
                    event.dateCreated ? formatInTimeZone(event.dateCreated, zone, "yyyy-MM-dd'T'HH:mm:ss.SSS") : "",
                    getFacilityEventTypeDisplayName(event.eventType) ?? "",
                    getDeviceName(event.deviceId),
                    getTriggerName(event),
                    formatEventValue(event),
                    event.externalTargetId ?? "",
                    t ? getTargetTypeDisplayName(t.type) ?? "" : "",
                    t?.fhwaClass ? getFHWAClassDisplayName(t.fhwaClass) ?? "" : "",
                    t?.lanes ? getLaneDisplayName(t.lanes as number) ?? "" : "",
                    t?.turn ? getTurnTypeDisplayName(t.turn) ?? "" : "",
                    toMph(t?.maximumVelocity),
                    toMph(t?.averageVelocity),
                    toMph(t?.minimumVelocity),
                    t?.maximumWidth?.toFixed(2) ?? "",
                    t?.maximumHeight?.toFixed(2) ?? "",
                    getImageUrl(event),
                ];
            });

            const csvContent = [headers.map((h) => `"${h}"`).join(","), ...rows.map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(","))].join("\n");

            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = `Events_${new Date().toISOString()}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error("Download failed:", error);
        }
    }, [isDeviceMode, events, facilityId, query, timeZone, zone, facility?.organizationId]);

    React.useImperativeHandle(ref, () => ({
        downloadCSV,
    }));

    // In facility mode, changing sort/filters gives fetchPage a new identity, which resets and
    // re-fetches the list. In device mode the local sort/filter state re-derives `events` in memory.
    const handleSort = (sortKey: string, direction: "asc" | "desc") => {
        if (isDeviceMode) {
            setLocalSortKey(sortKey);
            setLocalSortDirection(direction);
        } else {
            setQuery((prev) => ({ ...prev, sortKey, sortDirection: direction }));
        }
    };

    const handleFilter = (filters: FilterOption<FacilityEvent>[]) => {
        if (isDeviceMode) {
            setLocalFilters(filters);
        } else {
            setQuery((prev) => ({ ...prev, filters }));
        }
    };

    return (
        <>
            <SortableTable onRowClick={onRowClick} data={events} onSort={handleSort} onFilter={handleFilter} sortKey={isDeviceMode ? localSortKey : query.sortKey} sortDirection={isDeviceMode ? localSortDirection : query.sortDirection} isLoading={!isDeviceMode && isLoading} emptyValue={isDeviceMode ? "No events yet" : "No events found"} columns={displayColumns} filtering={true} mode="infiniteScroll" infiniteScroll={infiniteScrollOptions} />
            <ImageLightbox show={!!lightboxUrl} src={lightboxUrl} alt="Event snapshot" onClose={() => setLightboxUrl(undefined)} />
            {confirmDelete.renderDialog()}
        </>
    );
});

EventsTable.displayName = "EventsTable";
